import { Chip, Grid } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import { settings } from "app";
import BackButtonToolbar from "features/BackButtonToolbar";
import {
  FunctionField,
  Labeled,
  NumberField,
  ReferenceField,
  Show,
  SimpleShowLayout,
  TextField,
  UrlField,
  useRecordContext,
} from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { doctoralRequests } from "api/doctoralApi";
import { DoctoralServicePath } from "utils/constants";
import { DoctoralSeatStatusChoices } from "utils/choices";
import { instructionLanguageLabel } from "../seatLabel";
import DoctoralDateField from "../Partials/DoctoralDateField";
import { SectionTitle } from "features/SectionTitle";

const STATUS_COLORS = {
  1: "default",
  2: "info",
  3: "primary",
  4: "success",
  5: "error",
};

const StatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;
  const choice = DoctoralSeatStatusChoices.find(c => c.id === record.status);
  return (
    <Chip
      label={choice?.name ?? record.status}
      color={STATUS_COLORS[record.status] ?? "default"}
      size="small"
    />
  );
};

const FinalApprovalDate = () => {
  const record = useRecordContext();
  if (!record?.approvedByMonOn) return null;
  return (
    <Grid item xs={12} md={4}>
      <Labeled label="Окончателно одобрено на">
        <DoctoralDateField source="approvedByMonOn" showTime />
      </Labeled>
    </Grid>
  );
};

const DeclineSection = () => {
  const record = useRecordContext();
  if (!record?.declinedOn) return null;
  return (
    <>
      <Grid item xs={12} md={4}>
        <Labeled label="Отхвърлено на">
          <DoctoralDateField source="declinedOn" showTime />
        </Labeled>
      </Grid>
      <Grid item xs={12}>
        <Labeled label="Причина за отхвърляне">
          <TextField source="rejectionReason" />
        </Labeled>
      </Grid>
    </>
  );
};

const ShowDoctoralSeat = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  return (
    <Show
      title="Детайли за докторантско място"
      actions={<BackButtonToolbar />}
    >
      <SimpleShowLayout>
        <SectionTitle
          title="Основни данни"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid item xs={12} md={4}>
            <Labeled label="Област на висше образование">
              <ReferenceField
                source="higherEducationFieldId"
                reference={`${DoctoralServicePath}higher-education-fields`}
                link={false}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Докторантска програма">
              <ReferenceField
                source="doctoralProgramId"
                reference={doctoralRequests.getDoctoralProgramsSelect()}
                link={false}
              >
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Професионално направление">
              <ReferenceField
                source="professionalDirectionId"
                reference={administrationRequests.getProfessionalDirectionsSelect()}
                link={false}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Катедра">
              <ReferenceField
                source="departmentId"
                reference={administrationRequests.getDepartmentsSelect()}
                link={false}
              >
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Форма на обучение">
              <ReferenceField
                source="studyTypeId"
                reference={doctoralRequests.getDoctoralStudyTypesSelect()}
                link={false}
              >
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Изпит (дисциплина)">
              <ReferenceField
                source="doctoralDisciplineId"
                reference={doctoralRequests.getDoctoralDisciplinesSelect()}
                link={false}
              >
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Конспект по дисциплина">
              <UrlField source="disciplineConspectLink" target="_blank" rel="noopener noreferrer" />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Създадено на">
              <DoctoralDateField source="createdOn" showTime />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Брой места">
              <NumberField source="seatCount" />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Език на обучение">
              <FunctionField
                render={record =>
                  instructionLanguageLabel(record?.instructionLanguageId) || "—"
                }
              />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Година">
              <NumberField source="year" options={{ useGrouping: false }} />
            </Labeled>
          </Grid>
        </Grid>

        <SectionTitle
          title="Статус и одобрения"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid item xs={12} md={4}>
            <Labeled label="Статус">
              <FunctionField render={() => <StatusChip />} />
            </Labeled>
          </Grid>
          <FinalApprovalDate />
          <DeclineSection />
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};

export default ShowDoctoralSeat;
